import { t } from "@bezbolisno/i18n";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { ToastContext, type ToastTone } from "../lib/toast";

interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
}

const TTL_MS = 4500;
const MAX_VISIBLE = 3;

const ICON: Record<ToastTone, ReactNode> = {
  pos: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path d="M12 11v6M12 7.5v.5" strokeLinecap="round" />
    </svg>
  ),
  danger: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path d="M12 7v6M12 16.5v.5" strokeLinecap="round" />
    </svg>
  ),
};

/**
 * Transient confirmations, stacked bottom-right: a card per message that slides in, lives
 * for a few seconds (longer while hovered or focused) and can be closed by hand. One
 * provider around the router; screens call `useToast()`. Announced politely to screen
 * readers through `role="status"`.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setToasts((list) => list.filter((x) => x.id !== id));
  }, []);

  const schedule = useCallback(
    (id: number) => {
      const timer = timers.current.get(id);
      if (timer) clearTimeout(timer);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), TTL_MS),
      );
    },
    [dismiss],
  );

  const pause = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
  }, []);

  const toast = useCallback(
    (message: string, tone: ToastTone = "pos") => {
      seq.current += 1;
      const id = seq.current;
      setToasts((list) => [...list, { id, message, tone }].slice(-MAX_VISIBLE));
      schedule(id);
    },
    [schedule],
  );

  const api = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <section className="toaster" aria-label={t.shell.notifications}>
        {toasts.map((x) => (
          <output
            key={x.id}
            className={`toast toast--${x.tone}`}
            onMouseEnter={() => pause(x.id)}
            onMouseLeave={() => schedule(x.id)}
            onFocus={() => pause(x.id)}
            onBlur={() => schedule(x.id)}
          >
            <span className="toast__icon" aria-hidden="true">
              {ICON[x.tone]}
            </span>
            <span className="toast__text">{x.message}</span>
            <button
              type="button"
              className="toast__close"
              aria-label={t.common.dismiss}
              onClick={() => dismiss(x.id)}
            >
              ×
            </button>
          </output>
        ))}
      </section>
    </ToastContext.Provider>
  );
}
